import { useEffect, useState } from "react";
import { Wallet, Clock, ArrowDownCircle, ArrowUpCircle, DollarSign } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { WithdrawModal } from "@/components/WithdrawModal";
import { MovementsTable } from "@/components/MovementsTable";
import { WithdrawalsTable } from "@/components/WithdrawalsTable";

export default function AnfitriaoFinanceiro() {
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [saldoDisp, setSaldoDisp] = useState(0);
  const [saldoPend, setSaldoPend] = useState(0);
  const [movimentos, setMovimentos] = useState([]);
  const [saques, setSaques] = useState([]);
  const [loading, setLoading] = useState(true);

  function formatCurrency(v: number) {
    return (Number(v || 0) / 100).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  async function carregarFinanceiro() {
    try {
      setLoading(true);

      const response = await fetch("/anfitriao/financeiro/dados", {
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      setSaldoDisp(Number(data.saldoDisp ?? 0));
      setSaldoPend(Number(data.saldoPend ?? 0));
      setMovimentos(Array.isArray(data.movs) ? data.movs : []);
      setSaques(Array.isArray(data.saques) ? data.saques : []);
    } catch (error) {
      console.error("Erro ao carregar financeiro:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregarFinanceiro();
  }, []);

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="gradient-primary px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-primary-foreground" />
            <h1 className="text-2xl font-800 text-primary-foreground sm:text-3xl">
              Controle Financeiro
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card className="card-shadow hover-lift animate-fade-in-up border-0">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest/10">
                <Wallet className="h-6 w-6 text-forest" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Saldo Disponível</p>
                <p className="text-2xl font-800 text-forest">
                  {loading ? "Carregando..." : formatCurrency(saldoDisp)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow hover-lift animate-fade-in-up stagger-1 border-0">
            <CardContent className="flex items-center gap-4 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-coral/10">
                <Clock className="h-6 w-6 text-coral" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Saldo Pendente</p>
                <p className="text-2xl font-800 text-coral">
                  {loading ? "Carregando..." : formatCurrency(saldoPend)}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow hover-lift animate-fade-in-up stagger-2 flex items-center justify-center border-0">
            <CardContent className="w-full p-6">
              <Button
                onClick={() => setWithdrawOpen(true)}
                className="gradient-coral h-12 w-full rounded-xl text-base font-700 text-primary-foreground"
              >
                <ArrowUpCircle className="mr-2 h-5 w-5" />
                Solicitar Saque
              </Button>
            </CardContent>
          </Card>
        </div>

        <section className="animate-fade-in-up stagger-3">
          <h2 className="mb-4 flex items-center gap-2 text-xl font-700 text-foreground">
            <ArrowDownCircle className="h-5 w-5 text-forest" />
            Últimos Movimentos
          </h2>
          <Card className="card-shadow overflow-hidden border-0">
            <MovementsTable movements={movimentos} formatCurrency={formatCurrency} />
          </Card>
        </section>

        <section className="animate-fade-in-up">
          <h2 className="mb-4 flex items-center gap-2 text-xl font-700 text-foreground">
            <ArrowUpCircle className="h-5 w-5 text-coral" />
            Histórico de Saques
          </h2>
          <Card className="card-shadow overflow-hidden border-0">
            <WithdrawalsTable withdrawals={saques} formatCurrency={formatCurrency} />
          </Card>
        </section>
      </main>

      <WithdrawModal
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        saldoDisp={saldoDisp}
        formatCurrency={formatCurrency}
        onSuccess={carregarFinanceiro}
      />
    </div>
  );
}